import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  NgZone,
} from "@angular/core";
import { GoogleMap, MapInfoWindow } from "@angular/google-maps";
import { LocationData } from "src/app/models/tdsp/tdsp-custom-location";

@Component({
  selector: "app-location-selector",
  templateUrl: "./location-selector.component.html",
  styleUrls: ["./location-selector.component.scss"],
})
export class LocationSelectorComponent implements OnChanges, OnDestroy {
  @Input() locations: LocationData[] = [];
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  @Input() zoom = 6;
  @Input() centerRequest: { location: LocationData; zoom: number } | null =
    null;
  @Input() center: google.maps.LatLngLiteral = { lat: 20, lng: 0 }; // default world view;
  @Input() isEdit: boolean = false;

  @Output() radiusChanged = new EventEmitter<LocationData>();
  @Output() zoomChanged = new EventEmitter<number>();
  @Output() markerMoved = new EventEmitter<LocationData>();
  @Output() markerClicked = new EventEmitter<LocationData>();
  @Output() mapClicked = new EventEmitter<{
    lat: number;
    lng: number;
    name: string;
  }>();
  @Output() polygonDrawn = new EventEmitter<
    { lat: number; lng: number }[]
  >();
  @Output() polygonUpdated = new EventEmitter<{
    id: number;
    path: { lat: number; lng: number }[];
  }>();

  @ViewChild(GoogleMap) map: GoogleMap;

  mapOptions: google.maps.MapOptions = {
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true,
    maxZoom: 18,
    minZoom: 2,
    fullscreenControl: false,
    streetViewControl: false,
  };

  circles: Map<number, google.maps.Circle> = new Map();
  markers: Map<number, google.maps.Marker> = new Map();
  selectedLocation: any = null;

  drawingActive = false;
  canFinishDrawing = false;

  private geocoder: google.maps.Geocoder;
  private polygons: Map<number, google.maps.Polygon> = new Map();
  private polygonVertexMarkers: Map<number, google.maps.Marker[]> = new Map();
  private polygonIndexMarkers = new Map<number, google.maps.Marker>();
  private polygonDragCleanup: Map<number, Array<() => void>> = new Map();
  private drawingPath: google.maps.LatLng[] = [];
  private drawingOverlay: google.maps.Polygon | null = null;
  private drawingVertexMarkers: google.maps.Marker[] = [];

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit() {
    this.geocoder = new google.maps.Geocoder();

    // Map click listener
    this.map.googleMap?.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;

      if (this.drawingActive) {
        this.ngZone.run(() => this.addDrawingVertex(e.latLng!));
        return;
      }

      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        this.ngZone.run(() => {
          const name =
            status === "OK" && results[0]
              ? results[0].formatted_address
              : `Lat: ${lat}, Lng: ${lng}`;
          this.mapClicked.emit({ lat, lng, name });
        });
      });
    });

    if (this.locations.length > 0) {
      this.refreshMarkersAndCircles();
    }
  }

  openInfoWindow(marker, loc: any) {
    this.selectedLocation = loc;
    this.infoWindow.open(marker);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle navigation requests from parent
    if (changes["centerRequest"] && this.centerRequest) {
      const { location, zoom } = this.centerRequest;
      this.center = { lat: location.lat, lng: location.lng };
      this.zoom = zoom;
    }

    // Handle marker/circle refresh
    if (changes["locations"] && this.locations) {
      this.refreshMarkersAndCircles();
    }
  }

  private refreshMarkersAndCircles() {
    this.clearMap();

    this.locations.forEach((loc, index) => {
      const indexLabel = String(index + 1);
      if (loc.type === "polygon") {
        this.renderPolygon(loc, indexLabel);
        return;
      }

      const marker = new google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map: this.map.googleMap!,
        title: loc.name,
        draggable: true,
        label: {
          text: indexLabel,
          color: "#ffffff",
          fontSize: "12px",
          fontWeight: "600",
        },
      });

      const circle = new google.maps.Circle({
        center: { lat: loc.lat, lng: loc.lng },
        radius: loc.radius,
        map: this.map.googleMap!,
        editable: true,
      });

      this.markers.set(loc.id, marker);
      this.circles.set(loc.id, circle);

      // Circle radius changed
      circle.addListener("radius_changed", () => {
        const newRadius = Math.round(circle.getRadius());
        this.ngZone.run(() => {
          this.radiusChanged.emit({ ...loc, radius: newRadius });
          this.markerClicked.emit({ ...loc });
        });
      });

      // Marker dragged
      marker.addListener("dragend", () => {
        const newPos = marker.getPosition();
        if (newPos) {
          this.ngZone.run(() => {
            const lat = newPos.lat();
            const lng = newPos.lng();

            loc.lat = lat;
            loc.lng = lng;
            circle.setCenter(newPos);

            this.geocoder.geocode(
              { location: { lat, lng } },
              (results, status) => {
                this.ngZone.run(() => {
                  loc.name =
                    status === "OK" && results[0]
                      ? results[0].formatted_address
                      : `Lat: ${lat}, Lng: ${lng}`;
                  loc.title = loc.name;
                  this.markerMoved.emit({ ...loc });
                  this.markerClicked.emit({ ...loc });
                });
              }
            );
          });
        }
      });

      marker.addListener("click", () => {
        this.ngZone.run(() => {
          this.markerClicked.emit({ ...loc });
        });
      });

      circle.addListener("click", () => {
        this.ngZone.run(() => {
          this.markerClicked.emit({ ...loc });
        });
      });
    });
  }

  private renderPolygon(loc: LocationData, indexLabel: string): void {
    if (!loc.polygon?.length) return;

    const draggable = !this.isEdit;

    const polygon = new google.maps.Polygon({
      paths: loc.polygon,
      map: this.map.googleMap!,
      editable: draggable,
      draggable: false,
      clickable: true,
      fillColor: "#2563eb",
      fillOpacity: 0.18,
      strokeColor: "#2563eb",
      strokeOpacity: 1,
      strokeWeight: 3,
    });

    this.polygons.set(loc.id, polygon);
    this.renderPolygonVertexDots(loc.id, polygon);
    this.renderPolygonIndexMarker(loc.id, polygon, indexLabel);

    const emitUpdatedPath = () => {
      this.ngZone.run(() => {
        const path = polygon
          .getPath()
          .getArray()
          .map((p) => ({ lat: p.lat(), lng: p.lng() }));
        this.renderPolygonVertexDots(loc.id, polygon);
        this.renderPolygonIndexMarker(loc.id, polygon, indexLabel);
        this.polygonUpdated.emit({ id: loc.id, path });
      });
    };

    const path = polygon.getPath();
    google.maps.event.addListener(path, "insert_at", emitUpdatedPath);
    google.maps.event.addListener(path, "remove_at", emitUpdatedPath);
    google.maps.event.addListener(path, "set_at", emitUpdatedPath);

    polygon.addListener("click", (e: google.maps.PolyMouseEvent) => {
      (e as any).domEvent?.stopPropagation?.();
      this.ngZone.run(() => this.markerClicked.emit({ ...loc }));
    });

    if (draggable) {
      this.attachWholeShapeDrag(loc.id, polygon, emitUpdatedPath);
    }
  }

  private attachWholeShapeDrag(
    id: number,
    polygon: google.maps.Polygon,
    emitUpdatedPath: () => void,
  ): void {
    const googleMap = this.map.googleMap!;
    let dragging = false;
    let moved = false;
    let startLatLng: google.maps.LatLng | null = null;
    let originalPath: google.maps.LatLng[] = [];

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      this.setMapPanZoomEnabled(true);
      if (moved) emitUpdatedPath();
      startLatLng = null;
      originalPath = [];
    };

    const mouseDownListener = polygon.addListener(
      "mousedown",
      (e: google.maps.PolyMouseEvent) => {
        if (e.vertex !== undefined || e.edge !== undefined || !e.latLng) return;
        dragging = true;
        moved = false;
        startLatLng = e.latLng;
        originalPath = polygon.getPath().getArray().slice();
        this.setMapPanZoomEnabled(false);
      },
    );

    const mouseMoveListener = googleMap.addListener(
      "mousemove",
      (e: google.maps.MapMouseEvent) => {
        if (!dragging || !startLatLng || !e.latLng) return;

        const dLat = e.latLng.lat() - startLatLng.lat();
        const dLng = e.latLng.lng() - startLatLng.lng();
        if (!moved && Math.abs(dLat) < 1e-7 && Math.abs(dLng) < 1e-7) return;
        moved = true;

        const newPath = originalPath.map(
          (p) => new google.maps.LatLng(p.lat() + dLat, p.lng() + dLng),
        );
        polygon.setPath(newPath);
      },
    );

    const mouseUpListener = googleMap.addListener("mouseup", endDrag);
    window.addEventListener("mouseup", endDrag);

    this.polygonDragCleanup.set(id, [
      () => mouseDownListener.remove(),
      () => mouseMoveListener.remove(),
      () => mouseUpListener.remove(),
      () => window.removeEventListener("mouseup", endDrag),
    ]);
  }

  private setMapPanZoomEnabled(enabled: boolean): void {
    this.map?.googleMap?.setOptions({
      draggable: enabled,
      scrollwheel: enabled,
    });
  }

  private renderPolygonVertexDots(id: number, polygon: google.maps.Polygon): void {
    this.polygonVertexMarkers.get(id)?.forEach((m) => m.setMap(null));

    const dots = polygon
      .getPath()
      .getArray()
      .map(
        (point) =>
          new google.maps.Marker({
            position: point,
            map: this.map.googleMap!,
            clickable: false,
            zIndex: 999,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: "#fff",
              fillOpacity: 1,
              strokeColor: "#2563eb",
              strokeWeight: 3,
            },
          }),
      );

    this.polygonVertexMarkers.set(id, dots);
  }

  startDrawing(): void {
    if (this.isEdit || !this.map?.googleMap) return;
    this.drawingPath = [];
    this.canFinishDrawing = false;
    this.clearDrawingScratch();
    this.drawingActive = true;
  }

  cancelDrawing(): void {
    this.drawingActive = false;
    this.drawingPath = [];
    this.canFinishDrawing = false;
    this.clearDrawingScratch();
  }

  finishDrawing(): void {
    if (this.drawingPath.length < 3) return;

    const path = this.drawingPath.map((p) => ({ lat: p.lat(), lng: p.lng() }));

    this.drawingActive = false;
    this.drawingPath = [];
    this.canFinishDrawing = false;
    this.clearDrawingScratch();

    this.polygonDrawn.emit(path);
  }

  private addDrawingVertex(latLng: google.maps.LatLng): void {
    this.drawingPath.push(latLng);
    this.canFinishDrawing = this.drawingPath.length >= 3;
    this.renderDrawingScratch();
  }

  private renderDrawingScratch(): void {
    if (!this.map?.googleMap) return;

    if (!this.drawingOverlay) {
      this.drawingOverlay = new google.maps.Polygon({
        map: this.map.googleMap,
        paths: this.drawingPath,
        editable: false,
        draggable: false,
        clickable: false,
        fillColor: "#2563eb",
        fillOpacity: 0.12,
        strokeColor: "#2563eb",
        strokeOpacity: 0.9,
        strokeWeight: 2,
      });
    } else {
      this.drawingOverlay.setPath(this.drawingPath);
    }

    this.drawingVertexMarkers.forEach((m) => m.setMap(null));
    this.drawingVertexMarkers = this.drawingPath.map(
      (point) =>
        new google.maps.Marker({
          position: point,
          map: this.map.googleMap!,
          clickable: false,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: "#2563eb",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          },
        }),
    );
  }

  private clearDrawingScratch(): void {
    this.drawingOverlay?.setMap(null);
    this.drawingOverlay = null;
    this.drawingVertexMarkers.forEach((m) => m.setMap(null));
    this.drawingVertexMarkers = [];
  }

  clearMap() {
    this.circles.forEach((c) => c.setMap(null));
    this.markers.forEach((m) => m.setMap(null));
    this.polygons.forEach((p) => p.setMap(null));
    this.polygonVertexMarkers.forEach((dots) =>
      dots.forEach((m) => m.setMap(null)),
    );
    this.polygonIndexMarkers.forEach((m) => m.setMap(null));
    this.polygonDragCleanup.forEach((fns) => fns.forEach((fn) => fn()));
    this.setMapPanZoomEnabled(true);
    this.circles.clear();
    this.markers.clear();
    this.polygons.clear();
    this.polygonVertexMarkers.clear();
    this.polygonIndexMarkers.clear();
    this.polygonDragCleanup.clear();
  }

  onZoomChange() {
    const zoom = this.map.googleMap?.getZoom();
    if (zoom !== undefined) {
      this.zoomChanged.emit(zoom);
    }
  }

  updateCircleRadius(location: LocationData) {
    if (this.isEdit) return;
    const circle = this.circles.get(location.id);
    if (circle) {
      circle.setRadius(location.radius);
    }
  }

   /*Function for adding glyph markers for locations of type polygon*/
  private renderPolygonIndexMarker(id: number, polygon: google.maps.Polygon, indexLabel: string): void {
    this.polygonIndexMarkers.get(id)?.setMap(null);

    const points = polygon.getPath().getArray();
    if (!points.length) return;

    const centroid = points.reduce(
      (acc, p) => ({
        lat: acc.lat + p.lat() / points.length,
        lng: acc.lng + p.lng() / points.length,
      }),
      { lat: 0, lng: 0 },
    );

    const marker = new google.maps.Marker({
      position: centroid,
      map: this.map.googleMap!,
      clickable: false,
      draggable: false,
      zIndex: 1000,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 11,
        fillColor: "#2563eb",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
      },
      label: {
        text: indexLabel,
        color: "#ffffff",
        fontSize: "12px",
        fontWeight: "600",
      },
    });

    this.polygonIndexMarkers.set(id, marker);
  }

  ngOnDestroy() {
    this.clearMap();
    this.clearDrawingScratch();
  }
}